// avg-resolution-by-priority-chart.tsx — Avg resolution time by priority bar chart.
import { Chart, CHART_COLORS } from '@vnetwork/ui/charts';
import { Card, CardBody, CardTitle, Skeleton } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import type { TicketAvgPriority } from '@/api/tickets-statistics';

const PRIORITY_COLOR: Record<string, string> = {
  high: 'var(--vn-danger-solid)',
  medium: 'var(--vn-warning-solid)',
  low: 'var(--vn-success-solid)',
};

interface Props {
  data: TicketAvgPriority[] | undefined;
  isLoading: boolean;
  isError: boolean;
}

function fmtHours(v: string | number | undefined): string {
  const h = Number(v ?? 0);
  if (!isFinite(h) || h === 0) return '0h';
  if (h < 1) return `${Math.round(h * 60)}m`;
  return `${h.toFixed(1)}h`;
}

export function AvgResolutionByPriorityChart({ data, isLoading, isError }: Props) {
  const rows = (data ?? []).map((d) => ({
    label: d.ticket_priority.charAt(0).toUpperCase() + d.ticket_priority.slice(1),
    avg_hours: Number(d.avg_resolution_time ?? 0),
    color: PRIORITY_COLOR[d.ticket_priority] ?? CHART_COLORS[0],
  }));

  // Pivot each priority into its own series so per-priority colors render correctly.
  const pivotRows = rows.map((r) => ({ label: r.label, [r.label]: r.avg_hours }));
  const pivotSeries = rows.map((r) => ({
    key: r.label,
    label: r.label,
    color: r.color,
  }));

  return (
    <Card flat>
      <CardBody>
        <CardTitle style={{ marginBottom: 'var(--vn-space-3)' }}>Avg Resolution Time by Priority</CardTitle>
        {isError ? (
          <Alert tone="danger" title="Failed to load resolution data" />
        ) : isLoading ? (
          <Skeleton style={{ height: 200 }} />
        ) : rows.length === 0 ? (
          <EmptyState title="No data" description="No resolution data in this date range." />
        ) : (
          <Chart
            type="bar"
            data={pivotRows}
            xKey="label"
            series={pivotSeries}
            height={200}
            showGrid
            showXAxis
            showYAxis
            valueFormatter={fmtHours}
          />
        )}
      </CardBody>
    </Card>
  );
}
